import { useState, useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useToast } from '@/hooks/use-toast';
import { useFormations } from '@/hooks/useFormations';
import { CalendarIcon, Users, GraduationCap, UserPlus, X } from 'lucide-react';
import { ToolLogo } from '@/components/ui/ToolLogo';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const learnerSchema = z.object({
  firstName: z.string().min(2, 'Le prénom doit contenir au moins 2 caractères'),
  lastName: z.string().min(2, 'Le nom doit contenir au moins 2 caractères'),
  email: z.string().email('Email invalide'),
  phone: z.string().optional(),
  position: z.string().optional(),
});

const multiLearnerSchema = z.object({
  formationId: z.string().min(1, 'Formation requise'),
  startDate: z.date({
    required_error: 'Veuillez sélectionner une date de début',
  }),
  learners: z.array(learnerSchema).min(1, 'Au moins un apprenant requis'),
});

type MultiLearnerFormData = z.infer<typeof multiLearnerSchema>;

interface MultiLearnerFormProps {
  learnerCount: number;
  preselectedFormationId?: string;
  preselectedFormationTitle?: string;
  savedLearners?: Array<{
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    position?: string;
  }>;
  onSubmit: (data: MultiLearnerFormData) => Promise<void>;
  onCancel: () => void;
}

export function MultiLearnerForm({ 
  learnerCount, 
  preselectedFormationId, 
  preselectedFormationTitle,
  savedLearners = [],
  onSubmit, 
  onCancel 
}: MultiLearnerFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const { formations, loading: formationsLoading } = useFormations();
  const { toast } = useToast();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    control,
    reset
  } = useForm<MultiLearnerFormData>({
    resolver: zodResolver(multiLearnerSchema),
    defaultValues: {
      formationId: preselectedFormationId || '',
      learners: Array.from({ length: learnerCount }, () => ({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        position: '',
      }))
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'learners'
  });

  const watchedValues = watch();

  // Initialiser avec les apprenants sauvegardés ou créer des champs vides
  useEffect(() => {
    
    
    if (savedLearners.length > 0) {
      // Vider les champs existants
      while (fields.length > 0) {
        remove(0);
      }
      
      // Ajouter les apprenants sauvegardés avec un délai pour éviter les conflits
      setTimeout(() => {
        savedLearners.forEach((learner, index) => {
          append(learner);
          // Pré-remplir les valeurs
          Object.keys(learner).forEach(key => {
            setValue(`learners.${index}.${key}` as any, learner[key as keyof typeof learner]);
          });
        });
        
        // Mettre à jour la formation sélectionnée
        if (preselectedFormationId) {
          setValue('formationId', preselectedFormationId);
        }
        
        
      }, 100);
    } else {
      // Créer le nombre de champs requis si aucun apprenant sauvegardé
      const currentCount = fields.length;
      
      if (currentCount < learnerCount) {
        // Ajouter des champs manquants
        for (let i = currentCount; i < learnerCount; i++) {
          append({
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            position: '',
          });
        }
      } else if (currentCount > learnerCount) {
        // Retirer les champs en trop depuis la fin
        const fieldsToRemove = currentCount - learnerCount;
        for (let i = 0; i < fieldsToRemove; i++) {
          remove(currentCount - 1 - i);
        }
      }
      
    }
  }, [savedLearners, preselectedFormationId, setValue, learnerCount, append, remove, fields.length]);

  const onFormSubmit = async (data: MultiLearnerFormData) => {
    try {
      setIsSubmitting(true);
      
      // Validation côté client
      if (data.learners.length === 0) {
        
        toast({
          title: "Erreur",
          description: "Vous devez ajouter au moins un apprenant",
          variant: "destructive",
        });
        return;
      }

      await onSubmit(data);
      
      toast({ 
        title: 'Apprenants ajoutés', 
        description: `${data.learners.length} apprenant${data.learners.length > 1 ? 's ont' : ' a'} été ajouté${data.learners.length > 1 ? 's' : ''} avec succès` 
      });
      reset();
    } catch (error: any) {
      console.error('❌ Erreur lors de la soumission:', error);
      toast({
        title: 'Erreur',
        description: error.message || 'Impossible d\'ajouter les apprenants',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
      
    }
  };

  const selectedFormation = formations.find(f => f.id === watchedValues.formationId);

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
      {/* Formation et date */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <GraduationCap className="h-5 w-5" />
            Formation assignée
          </CardTitle>
          {preselectedFormationTitle && (
            <CardDescription>
              Formation présélectionnée : <span className="font-medium">{preselectedFormationTitle}</span>
            </CardDescription>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {selectedFormation && (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-start gap-4">
                {(() => {
                  const getToolFromCourseTitle = (title: string): 'Airtable' | 'Notion' | 'Make' | 'Zapier' | 'N8N' | 'Softr' | 'Bubble' | 'Webflow' | null => {
                    const titleLower = title.toLowerCase();
                    if (titleLower.includes('airtable')) return 'Airtable';
                    if (titleLower.includes('notion')) return 'Notion';
                    if (titleLower.includes('make')) return 'Make';
                    if (titleLower.includes('zapier')) return 'Zapier';
                    if (titleLower.includes('n8n')) return 'N8N';
                    if (titleLower.includes('softr')) return 'Softr';
                    if (titleLower.includes('bubble')) return 'Bubble';
                    if (titleLower.includes('webflow')) return 'Webflow';
                    return null;
                  };

                  const courseTool = getToolFromCourseTitle(selectedFormation.title);

                  return courseTool ? (
                    <div className="w-16 h-16 flex items-center justify-center bg-white rounded border shadow-sm">
                      <ToolLogo name={courseTool} size={48} />
                    </div>
                  ) : null;
                })()}
                
                <div className="flex-1">
                  <h4 className="font-semibold text-lg text-primary">{selectedFormation.title}</h4>
                </div>
              </div>
            </div>
          )}

          <div>
            <Label htmlFor="startDate">Date de début de formation *</Label>
            <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start text-left font-normal">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {watchedValues.startDate ? format(watchedValues.startDate, 'PPP', { locale: fr }) : 'Sélectionnez une date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={watchedValues.startDate}
                  onSelect={(date) => {
                    setValue('startDate', date!);
                    setIsDatePickerOpen(false);
                  }}
                  disabled={(date) => date < new Date()}
                  initialFocus
                  className="pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
            {errors.startDate && <p className="text-sm text-red-600 mt-1">{errors.startDate.message}</p>}
          </div>
        </CardContent>
      </Card>

      {/* Apprenants */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Informations des apprenants ({fields.length})
          </CardTitle>
          <CardDescription>
            Remplissez les informations de tous les participants en une seule fois
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {fields.map((field, index) => (
            <div key={field.id} data-testid={`learner-form-${index}`} data-learner-index={index} className="border rounded-lg p-4 space-y-4 bg-muted/20">
              <div className="flex items-center justify-between">
                <h4 className="font-medium flex items-center gap-2">
                  <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm">
                    {index + 1}
                  </span>
                  Apprenant {index + 1}
                </h4>
                {fields.length > 1 && (
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      remove(index);
                      
                      // S'assurer que l'état de soumission est réinitialisé
                      if (isSubmitting) {
                        setIsSubmitting(false);
                      }
                    }}
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    title="Supprimer cet apprenant"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor={`learners.${index}.firstName`}>Prénom *</Label>
                  <Input 
                    id={`learners.${index}.firstName`}
                    {...register(`learners.${index}.firstName` as const)} 
                    placeholder="Prénom" 
                  />
                  {errors.learners?.[index]?.firstName && (
                    <p className="text-sm text-red-600 mt-1">{errors.learners[index]?.firstName?.message}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor={`learners.${index}.lastName`}>Nom *</Label>
                  <Input 
                    id={`learners.${index}.lastName`}
                    {...register(`learners.${index}.lastName` as const)} 
                    placeholder="Nom de famille" 
                  />
                  {errors.learners?.[index]?.lastName && (
                    <p className="text-sm text-red-600 mt-1">{errors.learners[index]?.lastName?.message}</p>
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor={`learners.${index}.email`}>Email professionnel *</Label>
                <Input 
                  id={`learners.${index}.email`}
                  type="email" 
                  {...register(`learners.${index}.email` as const)} 
                  placeholder="prenom.nom@entreprise.com" 
                />
                {errors.learners?.[index]?.email && (
                  <p className="text-sm text-red-600 mt-1">{errors.learners[index]?.email?.message}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor={`learners.${index}.phone`}>Téléphone</Label>
                  <Input 
                    id={`learners.${index}.phone`}
                    {...register(`learners.${index}.phone` as const)} 
                    placeholder="06 12 34 56 78" 
                  />
                </div>
                <div>
                  <Label htmlFor={`learners.${index}.position`}>Poste / Fonction</Label>
                  <Input 
                    id={`learners.${index}.position`}
                    {...register(`learners.${index}.position` as const)} 
                    placeholder="Ex: Développeur, Chef de projet..." 
                  />
                </div>
              </div>
            </div>
          ))}

          {fields.length < 20 && (
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => append({
                firstName: '',
                lastName: '',
                email: '',
                phone: '',
                position: '',
              })}
              className="w-full"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Ajouter un autre apprenant
            </Button>
          )}
        </CardContent>
      </Card>

      <div className="flex gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
          Annuler
        </Button>
        <Button 
          type="submit" 
          disabled={isSubmitting || formationsLoading || fields.length === 0} 
          className="flex-1"
        >
          {isSubmitting ? 'Ajout en cours...' : `Ajouter ${fields.length} apprenant${fields.length > 1 ? 's' : ''}`}
        </Button>
      </div>
    </form>
  );
}